import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import { KpiCard, StatusBadge } from "@cx/ui";
import type { ChannelRow, DashboardCards, DashboardDre, GoalView, ProductSalesRow } from "@cx/contracts";
import type { Kpi } from "@cx/domain";
import {
  count,
  GOAL_METRIC_LABEL,
  goalPace,
  goalValue,
  money,
  pct,
  periodLabel,
  progressWidth,
  qty,
} from "./labels";

/** Os quatro KPIs de venda (RF-BI-01): valor, pedidos, itens e ticket médio, com a variação. */
export function SalesKpis({ cards }: { cards: DashboardCards }): ReactElement {
  const items: [string, Kpi, (v: number | null) => string, string][] = [
    ["Valor total de pedidos", cards.totalCents, money, "kpi-total"],
    ["Pedidos", cards.orders, count, "kpi-orders"],
    ["Itens vendidos", cards.itemsQ4, qty, "kpi-items"],
    ["Ticket médio", cards.avgTicketCents, money, "kpi-ticket"],
  ];
  return (
    <div className="row g-3 mb-3">
      {items.map(([label, k, fmt, id]) => (
        <div className="col-6 col-xl-3" key={id}>
          <KpiCard
            label={label}
            value={fmt(k.current)}
            previous={fmt(k.previous)}
            variationPct4={k.variationPct4}
            testId={id}
          />
        </div>
      ))}
    </div>
  );
}

/** DRE resumida (RF-BI-03). Custo, margem e lucro só chegam com products:read_cost. */
/** DRE do Mensal ou do consolidado (no grupo, imposto e taxa são os percentuais efetivos; sem faturamento, "—"). */
type DreView = Omit<DashboardDre, "taxPct4" | "salesFeePct4"> & {
  taxPct4: number | null;
  salesFeePct4: number | null;
};
export function DreTable({ dre }: { dre: DreView }): ReactElement {
  const rows: [string, Kpi | undefined, (v: number | null) => string, boolean][] = [
    ["Faturamento (sem frete)", dre.netRevenueCents, money, false],
    [`(−) Impostos (${pct(dre.taxPct4)})`, dre.taxesCents, money, true],
    [`(−) Taxas de venda (${pct(dre.salesFeePct4)})`, dre.feesCents, money, true],
    ["(−) Custo dos produtos", dre.costCents, money, true],
    ["Margem bruta", dre.grossMarginPct4, pct, false],
    ["(=) Lucro estimado", dre.profitCents, money, false],
    ["Lucro sobre o faturamento", dre.profitPct4, pct, false],
  ];
  return (
    <section className="box card h-100" aria-labelledby="dre-title" data-testid="dre">
      <div className="card-header">
        <h2 id="dre-title" className="h6 mb-0">
          DRE resumida
        </h2>
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">Conta</th>
              <th scope="col" className="text-end">
                Período
              </th>
              <th scope="col" className="text-end">
                Comparação
              </th>
              <th scope="col" className="text-end">
                Variação
              </th>
            </tr>
          </thead>
          <tbody>
            {rows
              .filter(([, k]) => k !== undefined)
              .map(([label, k, fmt]) => (
                <tr key={label}>
                  <th scope="row" className="fw-normal">
                    {label}
                  </th>
                  <td className="text-end">{fmt(k!.current)}</td>
                  <td className="text-end text-muted">{fmt(k!.previous)}</td>
                  <td className="text-end">{pct(k!.variationPct4)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {dre.costCents === undefined ? (
        <p className="small text-muted p-3 mb-0">Custo, margem e lucro exigem a permissão de ver custos.</p>
      ) : (
        !!dre.costMissingOrders && (
          <p className="small text-warning-emphasis p-3 mb-0" data-testid="cost-missing">
            {count(dre.costMissingOrders)} pedido(s) com item sem custo cadastrado: o custo está subestimado.
          </p>
        )
      )}
    </section>
  );
}

/** Ranking de produtos (Top SKUs ou produtos pai). */
export function ProductTable({
  title,
  rows,
  testId,
}: {
  title: string;
  rows: ProductSalesRow[];
  testId?: string;
}): ReactElement {
  const withCost = rows.some((r) => r.costCents !== undefined);
  return (
    <section className="box card h-100" aria-label={title} data-testid={testId}>
      <div className="card-header">
        <h2 className="h6 mb-0">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted p-3 mb-0">Sem vendas no período.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Produto</th>
                <th scope="col" className="text-end">
                  Qtde
                </th>
                <th scope="col" className="text-end">
                  Valor
                </th>
                {withCost && (
                  <th scope="col" className="text-end">
                    Custo
                  </th>
                )}
                <th scope="col" className="text-end">
                  Pedidos
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.productId}>
                  <td>
                    <Link to="/cadastros/produtos/$id" params={{ id: r.productId }}>
                      {r.name}
                    </Link>
                    {r.sku && <span className="text-muted small d-block">{r.sku}</span>}
                  </td>
                  <td className="text-end">{qty(r.qtyQ4)}</td>
                  <td className="text-end">{money(r.revenueCents)}</td>
                  {withCost && <td className="text-end">{money(r.costCents)}</td>}
                  <td className="text-end">{count(r.orders)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Vendas por canal (venda direta e lojas virtuais). */
export function ChannelTable({ rows, title = "Vendas por canal" }: { rows: ChannelRow[]; title?: string }) {
  const total = rows.reduce((a, r) => a + (r.totalCents ?? 0), 0);
  return (
    <section className="box card h-100" aria-label={title} data-testid="channels">
      <div className="card-header">
        <h2 className="h6 mb-0">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted p-3 mb-0">Sem vendas no período.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Canal</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
                <th scope="col" className="text-end">
                  Pedidos
                </th>
                <th scope="col" className="text-end">
                  Participação
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.label}</td>
                  <td className="text-end">{money(r.totalCents)}</td>
                  <td className="text-end">{count(r.orders)}</td>
                  <td className="text-end">
                    {r.totalCents === null || total <= 0
                      ? "—"
                      : pct(Math.round((r.totalCents / total) * 1_000_000))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Progresso de uma meta: barra, realizado × alvo e o ritmo esperado. */
export function GoalProgress({ g }: { g: GoalView }): ReactElement {
  const pace = goalPace(g);
  const w = progressWidth(g.progressPct4);
  return (
    <div className="mb-3" data-testid={`goal-${g.name}`}>
      <div className="d-flex justify-content-between align-items-baseline gap-2">
        <span className="fw-semibold text-truncate">{g.name}</span>
        <StatusBadge tone={pace.tone}>{pace.label}</StatusBadge>
      </div>
      <div className="small text-muted">
        {GOAL_METRIC_LABEL[g.metric]}
        {g.seller && ` · ${g.seller.name}`} · {periodLabel({ from: g.periodStart, to: g.periodEnd })}
      </div>
      <div
        className="progress my-1"
        role="progressbar"
        aria-label={`Progresso da meta ${g.name}`}
        aria-valuenow={Math.round(w)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={`progress-bar bg-${pace.tone}`} style={{ width: `${w}%` }} />
      </div>
      <div className="small">
        {goalValue(g.metric, g.actual)} de {goalValue(g.metric, g.target)} ({pct(g.progressPct4)})
        {g.state === "current" && g.metric !== "avg_ticket" && (
          <span className="text-muted"> · esperado {pct(g.elapsedPct4)}</span>
        )}
      </div>
    </div>
  );
}

export function GoalsCard({ goals }: { goals: GoalView[] }): ReactElement {
  return (
    <section className="box card h-100" aria-labelledby="goals-title" data-testid="goals-card">
      <div className="card-header">
        <h2 id="goals-title" className="h6 mb-0">
          Metas em andamento
        </h2>
      </div>
      <div className="card-body">
        {goals.length === 0 ? (
          <p className="text-muted mb-0">Nenhuma meta em andamento.</p>
        ) : (
          goals.map((g) => <GoalProgress key={g.id} g={g} />)
        )}
        {/* Fora do card-header: o azul do link sobre o cinza do cabeçalho não atinge 4,5:1. */}
        <Link to="/meu-negocio/metas" className="small">
          Ver todas as metas
        </Link>
      </div>
    </section>
  );
}
